import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { keyframes } from 'styled-components'
import phoneIcon from '../../../../assets/Icons/phone.svg'
import Checkbox from '../../../atoms/Login/Checkbox'
import InputComponent from '../../../atoms/Login/InputComponent'
import SubmitButton from '../../../atoms/Login/SubmitButton'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import { useNavigate } from 'react-router-dom'
import {
  setContactNumber,
  clearError,
  setRemeberMe,
} from '../../../../Redux/Slice/Auth/AuthSlice'
import { loginUser } from '../../../../Redux/ApiCalls/Auth/login'
import LoadingDots from '../../../atoms/Utlis/LoadinDots'
import { showErrorToast, showSuccessToast } from '../../../atoms/Utlis/Toast'
import sendFirebaseEvent from '../../../../analytics/FirebaseAnalytics'
import { trackClarityEvent } from '../../../../analytics/ClarityAnalytics'
import { CLARITY_EVENTS } from '../../../../analytics/EventTypes'
import { countryCodes } from '../../../../Utils/countryCodes'
import triangleIcon from '../../../../assets/Icons/login/triangle.webp'

const popIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const LoginFormContainer = styled.div`
  width: 100%;
  padding: 2.25rem 1.85rem 1.85rem;
  color: #141414;
  border-radius: 24px;
  background:
    linear-gradient(90deg, #ffc951, #ce56b0) top center / 100% 3px no-repeat,
    #ffffff;
  box-shadow:
    0 24px 48px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.08);
  position: relative;
  box-sizing: border-box;
  overflow: visible;
  animation: ${popIn} 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (min-width: 1440px) {
    padding: 2.75rem 2.35rem 2.15rem;
  }

  @media (max-width: 480px) {
    border-radius: 20px;
    padding: 1.75rem 1.35rem 1.5rem;
  }
`

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.35rem;
  align-items: stretch;
`

const StepRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

const StepIndex = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #ce56b0;
`

const ProgressTrack = styled.div`
  flex: 1;
  height: 3px;
  border-radius: 999px;
  background: rgba(20, 20, 20, 0.08);
  overflow: hidden;
`

const ProgressFill = styled.div`
  width: 42%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #ffc951, #ce56b0);
`

const FormHeading = styled.h2`
  font-family: 'Urbanist', sans-serif;
  font-size: clamp(30px, 4.5vw, 38px);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: #141414;
  margin: 0;
`

const HeadingAccent = styled.em`
  font-style: normal;
  background: linear-gradient(144.01deg, rgba(255, 201, 81) 0%, rgba(206, 86, 176) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`

const SubHeadingText = styled.p`
  font-family: 'Urbanist', sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 1.5;
  color: #5c5650;
  margin: 0;
`

const HeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  width: 100%;
`

const InputFields = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`

const Label = styled.label`
  font-family: 'Urbanist', sans-serif;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6a635c;
  text-align: left;
  width: 100%;
`

const PhoneFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  overflow: visible;
  position: relative;
  z-index: 10;
`

const CapsuleInputContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: #ffffff;
  border-radius: 18px;
  padding: 12px 18px;
  width: 100%;
  border: 1.5px solid rgba(20, 20, 20, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(20, 20, 20, 0.04);
  overflow: visible;
  position: relative;
  z-index: 5;

  &:focus-within {
    border-color: #ce56b0;
    box-shadow:
      0 0 0 4px rgba(206, 86, 176, 0.14),
      0 10px 24px rgba(206, 86, 176, 0.1);
  }
`

const CountryCodeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
  cursor: pointer;
  user-select: none;
  z-index: 20;
`

const DropdownContainer = styled.div`
  position: absolute;
  top: auto;
  bottom: calc(100% + 10px);
  left: 0;
  width: min(280px, 72vw);
  max-height: 240px;
  background: #ffffff;
  border: 1px solid rgba(20, 20, 20, 0.08);
  border-radius: 16px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeInUp 0.18s ease-out;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const SearchInputWrapper = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid #f0ece7;
  background-color: #ffffff;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e8e2da;
  border-radius: 10px;
  font-size: 13px;
  font-family: 'Urbanist', sans-serif;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #ce56b0;
  }
`

const CountryList = styled.div`
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f5f1ec;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
`

const CountryListItem = styled.div<{ active?: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  background-color: ${(props) => (props.active ? '#f9ecf7' : 'transparent')};

  &:hover {
    background-color: #f0ece7;
  }
`

const CountryFlag = styled.span`
  font-size: 16px;
  margin-right: 8px;
`

const CountryName = styled.span`
  font-size: 13px;
  font-family: 'Urbanist', sans-serif;
  color: #111111;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CountryDialCode = styled.span`
  font-size: 13px;
  font-family: 'Urbanist', sans-serif;
  font-weight: 600;
  color: #ce56b0;
  margin-left: 8px;
`

const Chevron = styled.span<{ $isOpen?: boolean }>`
  margin-left: -8px;
  width: 14px;
  height: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  cursor: pointer;
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'none')};

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
  }
`

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: #e4ddd4;
  margin-inline: 14px;
`

const PhoneImage = styled.img`
  width: 20px;
  height: 20px;
  opacity: 0.7;
`

const NumberInputWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`

const CheckBoxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  justify-content: flex-start;
`

const CheckBoxLabel = styled.label`
  font-family: 'Urbanist', sans-serif;
  font-weight: 500;
  font-size: 15px;
  color: #4a4540;
  cursor: pointer;
`

const SubmitButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.25rem;
  position: relative;
  min-height: 54px;
  width: 100%;
`

const BottomtextContainer = styled.div`
  width: 100%;
  align-items: center;
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 0.15rem;
  flex-wrap: wrap;
`

const FooterDivider = styled.span`
  color: #c9c1b8;
  font-size: 14px;
  user-select: none;
`

const StyledLink = styled.a`
  font-family: 'Urbanist', sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #6a635c;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #ce56b0;
  }
`

const LoginForm: React.FC = () => {
  const { t } = useTranslation()
  const [isChecked, setIsChecked] = useState(true)
  const [contact, setContact] = useState('')
  const [countryCode, setCountryCode] = useState('+91')
  const [activeButton, setActiveButton] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { apiStatus, loading, error, isError } = useAppSelector(
    (state) => state.auth
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    sendFirebaseEvent(
      'Login',
      'Preview_Page',
      'Load_Login_Page',
      'Load_Login_Page'
    )
  }, [])

  useEffect(() => {
    if (apiStatus === true) {
      showSuccessToast('OTP sent successfully.')
      navigate('/auth/otp')
    }
    return () => {
      dispatch(clearError())
    }
  }, [navigate, apiStatus])

  useEffect(() => {
    if (isError) {
      if (error) {
        if (error && error.message) {
          trackClarityEvent(CLARITY_EVENTS.API_ERROR, {
            source: 'login_form',
            message: error.message,
            status: error.status,
          })
          sendFirebaseEvent('Login', 'Error', 'login_error', error.message)
          showErrorToast(error.message)
        }
      }
    }

    return () => {
      dispatch(clearError())
    }
  }, [isError])

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length <= 10) {
      setContact(event.target.value)
      setActiveButton(event.target.value.length === 10)
    }
  }

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    const sanitizedPhone = `${countryCode}${contact}`

    if (validatePhoneNumber(contact)) {
      if (isChecked) {
        dispatch(setRemeberMe(true))
      }

      sendFirebaseEvent(
        'Login',
        'User_Interaction',
        'login_button_click',
        contact
      )
      trackClarityEvent(CLARITY_EVENTS.LOGIN_CLICKED, {
        phone_number: sanitizedPhone,
        remember_me: isChecked,
        country_code: countryCode,
        source: 'login_form',
      })

      dispatch(setContactNumber({ phone_number: contact, countryCode }))
      dispatch(
        loginUser({ phone_number: sanitizedPhone, country_code: countryCode })
      )
    } else {
      trackClarityEvent(CLARITY_EVENTS.VALIDATION_ERROR, {
        field: 'phone_number',
        reason: 'invalid_format',
        value_length: contact.length,
      })
    }
  }

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    return /^[0-9]{10}$/.test(phoneNumber)
  }

  return (
    <LoginFormContainer>
      <Form>
        <StepRow>
          <StepIndex>{t('login.step')}</StepIndex>
          <ProgressTrack>
            <ProgressFill />
          </ProgressTrack>
        </StepRow>

        <HeadingGroup>
          <FormHeading>
            {t('login.titlePrefix')}{' '}
            <HeadingAccent>{t('login.titleAccent')}</HeadingAccent>
          </FormHeading>
          <SubHeadingText>{t('login.subtitle')}</SubHeadingText>
        </HeadingGroup>

        <InputFields>
          <PhoneFieldGroup>
            <Label htmlFor="contactNo">{t('login.phoneNumber')}</Label>
            <CapsuleInputContainer>
              <CountryCodeWrapper
                ref={dropdownRef}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <PhoneImage src={phoneIcon} alt="" />
                <InputComponent
                  id="countryCode"
                  width={40}
                  value={countryCode}
                  name="countryCode"
                  placeholder=""
                  type="text"
                  readOnly={true}
                />
                <Chevron $isOpen={isDropdownOpen}>
                  <img src={triangleIcon} alt="" />
                </Chevron>
                {isDropdownOpen && (
                  <DropdownContainer onClick={(e) => e.stopPropagation()}>
                    <SearchInputWrapper>
                      <SearchInput
                        type="text"
                        placeholder="Search country..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </SearchInputWrapper>
                    <CountryList>
                      {countryCodes
                        .filter(
                          (c) =>
                            c.name
                              .toLowerCase()
                              .includes(searchQuery.toLowerCase()) ||
                            c.code.includes(searchQuery)
                        )
                        .map((c) => (
                          <CountryListItem
                            key={`${c.name}-${c.code}`}
                            active={countryCode === c.code}
                            onClick={(e) => {
                              e.stopPropagation()
                              setCountryCode(c.code)
                              setIsDropdownOpen(false)
                              setSearchQuery('')
                            }}
                          >
                            <CountryFlag>{c.flag}</CountryFlag>
                            <CountryName>{c.name}</CountryName>
                            <CountryDialCode>{c.code}</CountryDialCode>
                          </CountryListItem>
                        ))}
                    </CountryList>
                  </DropdownContainer>
                )}
              </CountryCodeWrapper>
              <Divider />
              <NumberInputWrapper>
                <InputComponent
                  id="contactNo"
                  width={100}
                  isPercent={true}
                  value={contact}
                  onChange={handleChange}
                  name="contact"
                  placeholder="Enter your phone number"
                  type="number"
                />
              </NumberInputWrapper>
            </CapsuleInputContainer>
          </PhoneFieldGroup>

          <CheckBoxContainer>
            <Checkbox
              id="checkbox"
              onChange={handleCheckboxChange}
              checked={isChecked}
            />
            <CheckBoxLabel htmlFor="checkbox">
              {t('login.rememberMe')}
            </CheckBoxLabel>
          </CheckBoxContainer>

          <SubmitButtonContainer>
            {loading ? (
              <LoadingDots />
            ) : (
              <SubmitButton
                onClick={handleSubmit}
                width={100}
                isPercent={true}
                text={t('login.send')}
                needArrow={true}
                active={loading ? true : !activeButton}
                textAlign="left"
              />
            )}
          </SubmitButtonContainer>

          <BottomtextContainer>
            <StyledLink onClick={() => navigate('/extra/privacy-policy')}>
              Privacy Policy
            </StyledLink>
            <FooterDivider>·</FooterDivider>
            <StyledLink onClick={() => navigate('/extra/Terms-and-condition')}>
              Terms of Use
            </StyledLink>
          </BottomtextContainer>
        </InputFields>
      </Form>
    </LoginFormContainer>
  )
}

export default LoginForm
